import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useTransition,
  useId,
  memo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Settings,
  Palette,
  CreditCard,
  User,
  Users,
  Zap,
  AlignJustify,
  ChevronDown,
  X,
  ArrowLeft,
  LogOut,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import GeneralSettingsContent from "../../components/settings/GeneralSettingsContent";
import AppearanceSettingsContent from "../../components/settings/AppearanceSettingsContent";
import SubscriptionSettingsContent from "../../components/settings/SubscriptionSettingsContent";
import ProfileSettingsContent from "../../components/settings/ProfileSettingsContent";
import IntegrationsSettingsContent from "../../components/settings/IntegrationsSettingsContent";
import TeamSettingsContent from "../../components/settings/TeamSettingsContent";

type SettingsTab =
  | "general"
  | "appearance"
  | "subscription"
  | "team"
  | "integrations"
  | "profile";

const SettingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { signOut } = useAuth();
  const componentId = useId(); // React 18: Stable unique ID for this component instance

  // React 18: useTransition for non-blocking tab switching
  // Keeps the UI responsive while tab content loads
  const [isPending, startTransition] = useTransition();

  // Get current tab from URL params, default to "general"
  const currentTab = (searchParams.get("tab") as SettingsTab) || "general";
  const [activeTab, setActiveTab] = useState<SettingsTab>(currentTab);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Sync activeTab with URL changes
  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  // React 18 Optimization: Use startTransition for non-urgent tab changes
  // This marks the tab content rendering as low-priority, keeping the UI responsive
  const handleTabChange = useCallback(
    (tab: SettingsTab) => {
      // Immediately update the active tab (urgent)
      setActiveTab(tab);

      // Mark URL update as non-urgent (can be interrupted)
      startTransition(() => {
        setSearchParams({ tab });
      });
    },
    [setSearchParams],
  );

  // Memoize handleSignOut
  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate("/login");
  }, [signOut, navigate]);

  // Memoize tabs array to prevent recreation
  const tabs = useMemo(
    () => [
      {
        id: "general" as const,
        label: "General",
        icon: Settings,
        description: "Organization setup and AI behavior",
      },
      {
        id: "appearance" as const,
        label: "Assistants Appearance",
        icon: Palette,
        description: "Branding and visual customization",
      },
      {
        id: "subscription" as const,
        label: "Subscription & Usage",
        icon: CreditCard,
        description: "Billing and plan management",
      },
      {
        id: "team" as const,
        label: "Team",
        icon: Users,
        description: "Invite and manage team members",
      },
      {
        id: "integrations" as const,
        label: "Integrations",
        icon: Zap,
        description: "Connect with external tools like Zapier",
      },
      {
        id: "profile" as const,
        label: "My Profile",
        icon: User,
        description: "Account and profile settings",
      },
    ],
    [],
  );

  // Memoize current tab for dropdown
  const currentTabData = useMemo(
    () => tabs.find((tab) => tab.id === activeTab),
    [tabs, activeTab],
  );

  // React 18: Memoize tab content rendering (only depends on activeTab)
  // Removed handleTabChange from dependencies - it's already memoized and doesn't affect rendering
  const renderTabContent = useMemo(() => {
    switch (activeTab) {
      case "general":
        return <GeneralSettingsContent />;
      case "appearance":
        return <AppearanceSettingsContent />;
      case "subscription":
        return <SubscriptionSettingsContent />;
      case "team":
        return <TeamSettingsContent />;
      case "integrations":
        return <IntegrationsSettingsContent />;
      case "profile":
        return <ProfileSettingsContent />;
      default:
        return <GeneralSettingsContent />;
    }
  }, [activeTab]);

  return (
    <MainLayout>
      <div className="flex h-full bg-gray-50">
        {/* Desktop Left Sidebar - Hidden on mobile */}
        <div
          className={`w-80 bg-white border-r border-gray-200 flex-shrink-0 ${isMobile ? "hidden" : ""}`}
        >
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage your organization and account preferences
            </p>
          </div>

          <ScrollArea className="h-[calc(100vh-140px)]">
            <nav className="p-4 space-y-2 flex flex-col h-full">
              <div className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-start gap-3 relative ${
                        activeTab === tab.id
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm flex items-center gap-2">
                          {tab.label}
                        </div>
                        <div
                          className={`text-xs mt-0.5 ${
                            activeTab === tab.id
                              ? "text-white/80"
                              : "text-gray-500"
                          }`}
                        >
                          {tab.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Logout Button at Bottom */}
              <div className="mt-auto pt-4 border-t border-gray-200">
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  <LogOut className="h-5 w-5 flex-shrink-0" />
                  <div className="font-medium text-sm">Sign Out</div>
                </button>
              </div>
            </nav>
          </ScrollArea>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Mobile Top Navigation */}
          {isMobile && (
            <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileSidebarOpen(true)}
                    className="h-10 w-10 p-0"
                  >
                    <AlignJustify className="h-5 w-5" />
                  </Button>
                  <h1 className="text-lg font-semibold text-gray-900">
                    Settings
                  </h1>
                </div>

                {/* Dropdown for settings pages */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      {currentTabData?.icon && (
                        <currentTabData.icon className="h-4 w-4" />
                      )}
                      {currentTabData?.label}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;

                      return (
                        <DropdownMenuItem
                          key={tab.id}
                          onClick={() => handleTabChange(tab.id)}
                          className={`flex items-center gap-3 p-3 cursor-pointer ${
                            activeTab === tab.id
                              ? "bg-[#8aa3d9] text-white"
                              : ""
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <div className="flex-1">
                            <div className="font-medium text-sm flex items-center gap-2">
                              {tab.label}
                            </div>
                            <div
                              className={`text-xs ${
                                activeTab === tab.id
                                  ? "text-white/80"
                                  : "text-gray-500"
                              }`}
                            >
                              {tab.description}
                            </div>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}

          {/* Content Area */}
          <ScrollArea className="h-full">
            <div className={`${isMobile ? "p-4" : "p-8"} relative`}>
              {/* React 18: Show transition pending indicator */}
              {isPending && (
                <div className="absolute top-4 right-4 flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg shadow-sm z-10">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm font-medium">Loading...</span>
                </div>
              )}
              {renderTabContent}
            </div>
          </ScrollArea>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobile && (
          <div
            className={`fixed inset-0 z-50 transition-all duration-300 ${
              isMobileSidebarOpen
                ? "opacity-100 visible"
                : "opacity-0 invisible"
            }`}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileSidebarOpen(false)}
            />

            {/* Sidebar Content */}
            <div
              className={`absolute left-0 top-0 h-full w-80 bg-white transform transition-transform duration-300 ${
                isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900">
                      Settings
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                      Manage your organization and account preferences
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <ScrollArea className="h-[calc(100vh-140px)]">
                <nav className="p-4 space-y-2 flex flex-col h-full">
                  <div className="space-y-2">
                    {/* Back to Dashboard Button */}
                    <button
                      onClick={() => {
                        navigate("/");
                        setIsMobileSidebarOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5 mb-4 border-b border-gray-200 pb-4"
                    >
                      <ArrowLeft className="h-5 w-5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">
                          Back to Dashboard
                        </div>
                        <div className="text-xs text-gray-500">
                          Return to main page
                        </div>
                      </div>
                    </button>

                    {tabs.map((tab) => {
                      const Icon = tab.icon;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            handleTabChange(tab.id);
                            setIsMobileSidebarOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-start gap-3 relative ${
                            activeTab === tab.id
                              ? "bg-[#8aa3d9] text-white"
                              : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                          }`}
                        >
                          <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm flex items-center gap-2">
                              {tab.label}
                            </div>
                            <div
                              className={`text-xs mt-0.5 ${
                                activeTab === tab.id
                                  ? "text-white/80"
                                  : "text-gray-500"
                              }`}
                            >
                              {tab.description}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Logout Button at Bottom */}
                  <div className="mt-auto pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMobileSidebarOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    >
                      <LogOut className="h-5 w-5 flex-shrink-0" />
                      <div className="font-medium text-sm">Sign Out</div>
                    </button>
                  </div>
                </nav>
              </ScrollArea>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
